import { useEffect, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import type {
  FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import Slider from 'LuxKit/Slider/Slider'
import Group from 'components/utils/Group'
import Caption from 'LuxKit/Typography/Caption'
import debounce from 'lodash.debounce'
import { convertMinutesToHoursAndMinutes } from 'lib/flights/flightUtils'

interface Props {
  items: Array<FilterPanelCheckItem>
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  selectedItem?: string
}

const STEP_NUM = 30

function FlightSingleFilterDuration(props: Props) {
  const intl = useIntl()
  const { items, onFilterChange, selectedItem } = props

  const maxNum = items[items.length - 1]?.value as number || 0
  const minNum = items[0]?.value as number || 0
  const min = (Math.floor(minNum / STEP_NUM) * STEP_NUM) + STEP_NUM
  const max = (Math.round(maxNum / STEP_NUM) * STEP_NUM) + STEP_NUM

  const [currentValue, setCurrentValue] = useState(selectedItem ? parseInt(selectedItem) : max)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentValue(parseInt(e.target.value))
    onChange(e)
  }

  // When we reset all the filters the value will be undefined so we need to set the value to max again
  useEffect(() => {
    if (!selectedItem) {
      setCurrentValue(max)
    }
  }, [selectedItem, max])

  const onChange = useMemo(() => {
    return debounce(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFilterChange(e)
      },
      200,
    )
  }, [onFilterChange])

  const minTime = convertMinutesToHoursAndMinutes(min)
  const minTimeStr = intl.formatMessage(
    {
      id: 'MWrcyLLLak4iiHbMl3Ge',
      defaultMessage: '{hours}h {minutes}m',
      description: 'Duration format showing hours and minutes for flight duration filter minimum',
    },
    { hours: minTime.hours, minutes: minTime.minutes },
  )
  const currentValueTime = convertMinutesToHoursAndMinutes(currentValue)
  const timeStr = intl.formatMessage(
    {
      id: 'qA_0C9B3_HDs19pbZdfv',
      defaultMessage: '{hours}h {minutes}m',
      description: 'Duration format showing hours and minutes for flight duration filter current value',
    },
    { hours: currentValueTime.hours, minutes: currentValueTime.minutes },
  )

  return (
    <Group direction="vertical" gap={4}>
      <Slider
        name="durations"
        min={min}
        max={max}
        onChange={handleChange}
        step={STEP_NUM}
        value={currentValue}
      />

      <Group direction="horizontal" horizontalAlign="space-between">
        <Caption variant="large" colour="light-foreground-alt" align="start">
          {minTimeStr}
        </Caption>

        <Caption variant="large" colour="light-foreground-alt" align="end">
          {timeStr}
        </Caption>
      </Group>
    </Group>
  )
}

export default FlightSingleFilterDuration
